import styled from "styled-components";
import { Text } from "@lmn/design-system";
import media from "@/styles/mediaQueries";

export const TabsRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  // Center the group: when the tabs fit they sit centered with the chevrons
  // hugging them; when they overflow the viewport shrinks + scrolls instead.
  justifyContent: "center",
  gap: theme.kallisto.space.space100,
  padding: theme.kallisto.space.space200,
  paddingBottom: theme.kallisto.space.space100,
  // Mirror the price-bar calendar's CalendarContainer: the card lives inside an
  // Accordion grid whose implicit `auto` column grows to its content's
  // max-content. Without containment the non-shrinking tabs widen that column
  // past the viewport and blow out the card on mobile. `contain: inline-size`
  // ties this row's width to the card (not its tabs) so the viewport scrolls
  // internally instead.
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  contain: "inline-size",
}));

// Horizontally scrollable row of tabs — finger-swipe on mobile, same as the
// price-bar calendar. Chevrons (below) are an extra control on desktop only.
export const TabsViewport = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kallisto.space.space100,
  // Width = content, but shrinks (and scrolls) when the tabs exceed the row.
  flex: "0 1 auto",
  minWidth: 0,
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

export const TabNavButton = styled.button<{ $disabled?: boolean }>(
  ({ theme, $disabled }) => ({
    // Hidden on mobile (tabs scroll by touch), shown from sm+ — mirrors the
    // calendar's NavButton so both behave the same.
    display: "none",
    ...media.screenSmMin({
      display: "flex",
    }),
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    minWidth: "32px",
    flexShrink: 0,
    alignSelf: "center",
    borderRadius: theme.kallisto.border.radius.radiusFull,
    border: `1px solid ${theme.kallisto.color.border.static.borderNeutral}`,
    backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.4 : 1,
    transition: "background-color 0.1s ease",

    "&:hover": {
      backgroundColor: $disabled
        ? theme.kallisto.color.background.static.backgroundLayer01
        : theme.kallisto.color.background.interactive.backgroundNeutralHover,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.kallisto.color.common.black[1000]}`,
      outlineOffset: "2px",
    },

    svg: {
      width: "14px",
      height: "14px",
      fill: theme.kallisto.color.icon.static.iconPrimary,
    },
  }),
);

export const Tab = styled.button<{ $isActive?: boolean }>(
  ({ theme, $isActive }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.kallisto.space.space50,
    minWidth: "84px",
    // Never shrink/grow: tabs keep a stable width and overflow into the
    // scrollable viewport instead of cramming on narrow screens.
    flexShrink: 0,
    flexGrow: 0,
    // Keep equal box height regardless of the 1px/2px border by compensating padding.
    padding: $isActive
      ? `${theme.kallisto.space.space100} ${theme.kallisto.space.space150}`
      : `calc(${theme.kallisto.space.space100} + 1px) calc(${theme.kallisto.space.space150} + 1px)`,
    borderRadius: theme.kallisto.border.radius.radius150,
    border: `${$isActive ? "2px" : "1px"} solid ${
      $isActive
        ? theme.kallisto.color.border.interactive.borderPrimaryDefault
        : theme.kallisto.color.border.static.borderNeutral
    }`,
    backgroundColor: theme.kallisto.color.background.static.backgroundLayer01,
    cursor: $isActive ? "default" : "pointer",
    font: "inherit",
    transition: "border-color 0.1s ease",

    "&:hover": {
      borderColor: $isActive
        ? theme.kallisto.color.border.interactive.borderPrimaryHover
        : theme.kallisto.color.border.interactive.borderPrimaryDefault,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.kallisto.color.common.black[1000]}`,
      outlineOffset: "2px",
    },
  }),
);

// Minimal wrappers: only keep `white-space: nowrap` so the tab grows to its
// content (label/price stay single-line) instead of wrapping. Color + weight
// are passed on the <Text> element via its `color` / `weight` props.
export const TabLabel = styled(Text)({
  whiteSpace: "nowrap",
});

export const TabPrice = styled(Text)({
  whiteSpace: "nowrap",
});
